// Type importé depuis `preact` directement : l'homonyme de l'espace de noms
// `JSX` est déprécié, et celui de la surface de compatibilité React aussi.
import type { HTMLAttributes } from 'preact';
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * Équivalent minimal de `@radix-ui/react-slot`.
 *
 * shadcn s'appuie sur `asChild` pour qu'un composant délègue son rendu à son
 * enfant — un `Button asChild` qui enveloppe un lien produit un `<a>` stylé en
 * bouton, et non un bouton contenant un lien, qui serait un balisage fautif.
 *
 * Le paquet de Radix n'est pas installable ici : il dépend d'internes de React
 * que `preact/compat` ne couvre pas. Le comportement tient de toute façon en un
 * `cloneElement` — les propriétés du parent sont posées sur l'enfant, et les
 * deux jeux de classes sont fusionnés dans l'ordre attendu (celles de l'enfant
 * l'emportent, puisqu'elles sont les plus spécifiques à l'appel).
 */
/**
 * `ref` est retiré du type puis réadmis en `unknown`. Il est **invariant** :
 * une `Ref<HTMLButtonElement>` venue de `Button` n'est pas assignable à une
 * `Ref<HTMLElement>`, alors que Slot ignore la référence — il la repasse à
 * l'enfant, qui seul connaît son élément. Desserrer ce point précis vaut mieux
 * que relâcher tout le type, qui garde ainsi ses attributs vérifiés.
 */
interface ProprietesSlot extends Omit<HTMLAttributes<HTMLElement>, 'ref'> {
  readonly ref?: unknown;
  readonly children?: ReactNode;
}

export function Slot({ children, className, ...reste }: ProprietesSlot) {
  if (!isValidElement(children)) {
    throw new Error(
      "Slot attend un unique élément pour enfant : `asChild` a été posé sur un composant qui n'en fournit pas.",
    );
  }

  const enfant = children as ReactElement<{ className?: string }>;

  return cloneElement(enfant, {
    ...reste,
    className: cn(className, enfant.props.className),
  });
}
